/**
 * sharedRealmSync — client-side background task for shared realm tasks.
 *
 * Problem: startAtIndex and reminders are per-user (private realm). When another
 * member adds/modifies a task in a shared list, the task syncs but the member's
 * local startAtIndex and reminders are NOT created automatically.
 *
 * Solution: On app-start and visibility-change (i.e. app foregrounded), scan
 * shared realm tasks and ensure each one has a local startAtIndex entry.
 * Also creates local reminders for tasks in containers/with prefs that have
 * enableReminders=1.
 *
 * This complements:
 * - `ensureSharedInstances.ts` (recurring only — called from recurrenceFallback)
 * - `useSharedReminderSync.ts` (cleanup only — removes stale reminders)
 * - DO fan-out (server-side — creates entries when webhook fires)
 *
 * This task is the "lazy client-side" fallback that catches anything missed.
 *
 * NOTE: The `startAt` field is NOT in the DbObject TypeScript interface but
 * MAY exist on the raw IndexedDB object as denormalized data. We access it
 * via type-widening (Record<string, unknown>). If absent, we skip.
 */

import { db } from "@/db/schema"
import {
  ObjectStatus,
  ObjectType,
  StartAtType,
  StartAtSubType,
  parseDbDate,
} from "@totodo/operations"
import type { DbObject } from "@totodo/operations"
import dayjs from "dayjs"
import type { BackgroundTaskDef } from "@/background/types"

// ---------------------------------------------------------------------------
// Type helpers — raw DB object may have fields not in DbObject interface
// ---------------------------------------------------------------------------

/** Raw task as stored in IndexedDB (includes denormalized schedule fields). */
type RawTask = DbObject & Record<string, unknown>

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Debounce: don't re-run within 2 minutes (visibility-change can fire often). */
const MIN_INTERVAL_MS = 2 * 60 * 1000
const LAST_RUN_KEY = "#shared-realm-sync-last-run"

// ---------------------------------------------------------------------------
// Task definition
// ---------------------------------------------------------------------------

export const sharedRealmSyncTask: BackgroundTaskDef = {
  id: "shared-realm-sync",
  description:
    "Ensure shared realm tasks have local startAtIndex entries + reminders",
  priority: 9, // After recurrence-fallback (8), before telemetry (11+)
  triggers: ["app-start", "visibility-change"],
  initialDelayMs: 4000, // 4s — let sync complete first

  async run(_signal) {
    // Debounce rapid visibility changes
    const lastRun = await getLastRunTime()
    if (lastRun && Date.now() - lastRun < MIN_INTERVAL_MS) return

    const entriesCreated = await ensureSharedTasksHaveStartAtIndex()
    const remindersCreated = await ensureSharedReminders()

    if (entriesCreated > 0 || remindersCreated > 0) {
      console.info(
        `[shared-realm-sync] Created ${entriesCreated} startAtIndex entries, ${remindersCreated} reminders`,
      )
    }

    await setLastRunTime(Date.now())
  },
}

// ---------------------------------------------------------------------------
// Core: startAtIndex for all shared tasks
// ---------------------------------------------------------------------------

/**
 * Find shared realm tasks (non-recurring) that have a startAt date (denormalized
 * on the raw DB object or from webhook fan-out) but are missing a local
 * startAtIndex entry, and create one.
 *
 * Recurring instances are handled by `ensureSharedInstances.ts` (called from
 * recurrenceFallbackTask). This function handles everything else.
 */
async function ensureSharedTasksHaveStartAtIndex(): Promise<number> {
  // Get prefs that have showInDateViews enabled (container + task level)
  // NOTE: Dexie Cloud automatically filters out soft-deleted records from queries
  const containerPrefs = await db.sharedViewPref
    .filter((p) => p.showInDateViews === 1 && p.scope === "container")
    .toArray()
  const taskPrefs = await db.sharedViewPref
    .filter((p) => p.showInDateViews === 1 && p.scope === "task")
    .toArray()

  if (!containerPrefs.length && !taskPrefs.length) return 0

  const containerPrefMap = new Map(
    containerPrefs.map((p) => [p.id.slice(1), p]),
  )
  const taskPrefMap = new Map(taskPrefs.map((p) => [p.id.slice(1), p]))

  // Get all unfinished tasks in shared realms that are NOT recurring instances
  // Walk the index of live, unfinished tasks and filter by realm in JS. A plain
  // db.totodo.filter() cursors through every object in the table (~23K on a
  // large account) inside one readonly transaction, and IndexedDB will not
  // start a readwrite transaction on totodo until it finishes: at startup
  // Dexie Cloud's sync and the user's first addTask queued behind it for
  // 10-20s on a mid-range device. (The realmId index is no better — a member
  // of busy shared lists can have >10K objects under "rlm~".)
  //
  // Plain equals().toArray() is one getAll(); a Dexie .filter() here would
  // iterate a cursor with a main-thread round trip per row instead.
  const tasks = (
    await db.totodo
      .where("[type+isDeleted+status]")
      .equals([ObjectType.Task, 0, ObjectStatus.Unfinished])
      .toArray()
  ).filter(
    (t) =>
      !t.recurringTemplateId && !!t.realmId && t.realmId.startsWith("rlm~"),
  ) as RawTask[]

  if (!tasks.length) return 0

  // Only consider tasks that have a startAt field AND are in an opted-in container/task
  const tasksWithDate = tasks.filter((t) => {
    const startAt = t.startAt as string | undefined
    if (!startAt || parseDbDate(startAt) === undefined) return false

    // Check per-task pref first, then container pref
    if (taskPrefMap.has(t.id!)) return true
    const containerId = t.realmId!.replace("rlm~", "")
    return containerPrefMap.has(containerId)
  })

  if (!tasksWithDate.length) return 0

  // Batch-check which ones already have entries
  const entryIds = tasksWithDate.map((t) => `#${t.id!}`)
  const existing = await db.startAtIndex.bulkGet(entryIds)
  const existingSet = new Set(
    existing.filter(Boolean).map((e) => e!.id as string),
  )

  const missing = tasksWithDate.filter((t) => !existingSet.has(`#${t.id!}`))
  if (!missing.length) return 0

  // Get next index
  const lastIdx = await db.startAtIndex.orderBy("index").last()
  let nextIndex = (lastIdx?.index ?? 0) + 1

  const entries: Array<{
    id: string
    index: number
    startAt: string
    type: StartAtType
    subType: StartAtSubType
  }> = []

  for (const task of missing) {
    const startAtMs = parseDbDate(task.startAt as string | undefined)
    if (startAtMs === undefined) continue
    const startAt = String(startAtMs)
    const startAtType = (task.startAtType as number) ?? StartAtType.Default
    const startAtSubType = (task.startAtSubType as number) ?? StartAtSubType.Default

    entries.push({
      id: `#${task.id!}`,
      index: nextIndex++,
      startAt,
      type: startAtType as StartAtType,
      subType: startAtSubType as StartAtSubType,
    })
  }

  if (entries.length) {
    await db.startAtIndex.bulkAdd(entries).catch(() => {
      // Ignore ConstraintError from duplicate IDs (race with sync/DO)
    })
  }

  // Update pass: fix stale entries where startAt/type/subType changed
  const existingEntries = existing.filter(Boolean) as Array<{
    id: string; startAt?: string; type?: number; subType?: number
  }>
  let updated = 0
  for (const entry of existingEntries) {
    const taskId = entry.id.slice(1) // "#taskId" → "taskId"
    const task = tasksWithDate.find((t) => t.id === taskId)
    if (!task) continue
    const startAtMs = parseDbDate(task.startAt as string | undefined)
    if (startAtMs === undefined) continue
    const startAt = String(startAtMs)
    const startAtType = (task.startAtType as number) ?? StartAtType.Default
    const startAtSubType = (task.startAtSubType as number) ?? StartAtSubType.Default
    if (entry.startAt !== startAt || entry.type !== startAtType || entry.subType !== startAtSubType) {
      try {
        await db.startAtIndex.update(entry.id, {
          index: startAtMs,
          startAt,
          type: startAtType as StartAtType,
          subType: startAtSubType as StartAtSubType,
        })
        updated++
      } catch { /* ignore race */ }
    }
  }

  return entries.length + updated
}

// ---------------------------------------------------------------------------
// Core: reminders for shared tasks with enableReminders pref
// ---------------------------------------------------------------------------

/**
 * For tasks in containers with enableReminders=1 (or per-task pref),
 * create local reminder entries if the task has a start date (from which
 * we derive reminder time using the pref's offset) but no local reminder.
 */
async function ensureSharedReminders(): Promise<number> {
  // Get container-level prefs
  const containerPrefs = await db.sharedViewPref
    .filter((p) => p.enableReminders === 1 && p.scope === "container")
    .toArray()

  // Get task-level prefs
  const taskPrefs = await db.sharedViewPref
    .filter((p) => p.enableReminders === 1 && p.scope === "task")
    .toArray()

  if (!containerPrefs.length && !taskPrefs.length) return 0

  const containerPrefMap = new Map(
    containerPrefs.map((p) => [p.id.slice(1), p]),
  )
  const taskPrefMap = new Map(taskPrefs.map((p) => [p.id.slice(1), p]))

  // Find shared tasks that have a start date and are in an enabled container/task
  // Index walk, not a full-table filter() — see ensureSharedTasksHaveStartAtIndex.
  const sharedTasks = (
    await db.totodo
      .where("[type+isDeleted+status]")
      .equals([ObjectType.Task, 0, ObjectStatus.Unfinished])
      .toArray()
  ).filter((t) => !!t.realmId && t.realmId.startsWith("rlm~")) as RawTask[]

  if (!sharedTasks.length) return 0

  // Filter: must have a start date AND be in an enabled container/task
  const eligibleTasks = sharedTasks.filter((t) => {
    const startAt = t.startAt as string | undefined
    if (!startAt || parseDbDate(startAt) === undefined) return false

    // Check per-task pref first, then container pref
    if (taskPrefMap.has(t.id!)) return true
    const containerId = t.realmId!.replace("rlm~", "")
    return containerPrefMap.has(containerId)
  })

  if (!eligibleTasks.length) return 0

  let created = 0
  const now = dayjs().valueOf()

  for (const task of eligibleTasks) {
    const startAt = parseDbDate(task.startAt as string | undefined)
    if (startAt === undefined) continue
    const containerId = task.realmId!.replace("rlm~", "")
    const pref = taskPrefMap.get(task.id!) ?? containerPrefMap.get(containerId)
    const offset = pref?.reminderOffsetMs ?? -3600000 // Default: 1h before
    const fireAt = startAt + offset

    if (fireAt <= now) continue // Past — don't create

    const reminderId = `shared-${task.id!}`
    const title = task.title || "Reminder"

    try {
      // Check if shared-sync reminder already exists
      const existing = await db.reminder.get(reminderId)

      if (existing && !existing.isDeleted) {
        // GAP-6 fix: Update if title or fireAt changed
        const existingFireAt = parseDbDate(existing.fireAt)
        if (existingFireAt !== fireAt || existing.title !== title) {
          await db.reminder.update(reminderId, {
            fireAt: String(fireAt),
            title,
          })
          created++
        }
      } else {
        await db.reminder.put({
          id: reminderId,
          taskId: task.id!,
          parentPath: task.parentPath ?? "",
          owner: db.cloud.currentUserId ?? "",
          title,
          realmId: task.realmId!,
          sourceType: "internal",
          triggerType: "time",
          fireAt: String(fireAt),
          kind: "reminder",
          createdAt: new Date().toISOString(),
          createdBy: db.cloud.currentUserId ?? "shared-realm-sync",
          isDeleted: 0,
        })
        created++
      }
    } catch {
      // Ignore duplicates (race with sync/DO)
    }
  }

  return created
}

// ---------------------------------------------------------------------------
// Helpers — persist last-run timestamp
// ---------------------------------------------------------------------------

async function getLastRunTime(): Promise<number | null> {
  try {
    const entry = await db.table("setting_local").get(LAST_RUN_KEY)
    return entry?.value ? Number(entry.value) : null
  } catch {
    return null
  }
}

async function setLastRunTime(ms: number): Promise<void> {
  try {
    await db.table("setting_local").put({
      id: LAST_RUN_KEY,
      value: String(ms),
      device: "local",
      createdAt: new Date().toISOString(),
      createdBy: "system",
    })
  } catch {
    // Best effort
  }
}
